import { APIFieldAttributes, ArdoqId } from '@ardoq/api-types';
import { SpiderChartPoint } from './view/types';
import { sum, mean } from 'lodash';
import { ComponentTypeForLegend, ViewLegendRowModel } from '@ardoq/view-legend';
import { getRangeColor } from './view/utils';
import { isTruthy } from '@ardoq/common-helpers';
import { componentOperations, toIds } from '@ardoq/core';
import type { ComponentsState } from 'streams/components/components$';
import { ChildAggregationFunction } from './types';
import { getComponentLabelParts } from '@ardoq/graph';
import { fieldUtils } from '@ardoq/scope-data';
import type { I18nLocale } from '@ardoq/i18n';

const formatNumericFieldValue = (
  field: APIFieldAttributes,
  value: number,
  i18nLocale: I18nLocale
): string =>
  fieldUtils.fieldAttributesToLabel({
    fieldAttributes: field,
    fieldType: field.type,
    value,
    i18nLocale,
    formatUserFieldFn: () => null,
  }) ?? `${value}`;

const getFieldValueAsNumeric = (
  components: ComponentsState,
  componentId: ArdoqId,
  fieldName: string
) => {
  const fieldValue = components.byId[componentId]?.[fieldName] ?? null;

  if (Number.isFinite(fieldValue)) {
    return fieldValue as number;
  }

  return 0;
};

const getChartPointValue = (
  components: ComponentsState,
  componentId: ArdoqId,
  fieldName: string,
  childAggregationFunction: ChildAggregationFunction
) => {
  if (childAggregationFunction === 'zero') {
    return getFieldValueAsNumeric(components, componentId, fieldName);
  }

  const root = components.byId[componentId];
  if (!root) return 0;

  const descendantIds = toIds(
    componentOperations.getFlatHierarchy([root], components.childrenByParent)
  );

  const values = descendantIds.map(descendantComponentId =>
    getFieldValueAsNumeric(components, descendantComponentId, fieldName)
  );

  switch (childAggregationFunction) {
    case 'sum':
      return sum(values);
    case 'average':
      return mean(values);
  }
};

const getDataPoint = (
  components: ComponentsState,
  componentId: ArdoqId,
  field: APIFieldAttributes,
  childAggregationFunction: ChildAggregationFunction,
  isFlipped: boolean,
  i18nLocale: I18nLocale
) => {
  const value = getChartPointValue(
    components,
    componentId,
    field.name,
    childAggregationFunction
  );

  const labelInfo = isFlipped
    ? { label: field.label, fieldLabel: null, fieldValue: null }
    : getComponentLabelParts(componentId);

  const fieldId = field._id;
  return {
    id: `${componentId}~~${fieldId}`,
    componentId,
    labelInfo,
    value,
    formattedValue: formatNumericFieldValue(field, value, i18nLocale),
  };
};

type GetChartPointsArgs = {
  components: ComponentsState;
  relevantComponentIds: ArdoqId[];
  numericFields: APIFieldAttributes[];
  selectedFieldNames: Record<string, boolean>;
  childAggregationFunction: ChildAggregationFunction;
  isFlipped: boolean;
  i18nLocale: I18nLocale;
};

export const getChartPoints = ({
  components,
  relevantComponentIds,
  numericFields,
  selectedFieldNames,
  childAggregationFunction,
  isFlipped,
  i18nLocale,
}: GetChartPointsArgs): SpiderChartPoint[][] => {
  if (relevantComponentIds.length < 1) {
    return [];
  }

  const selectedFieldNamesList = Object.keys(selectedFieldNames).filter(
    name => selectedFieldNames[name]
  );

  const fields = numericFields.filter(field =>
    selectedFieldNamesList.includes(field.name)
  );

  if (isFlipped) {
    return relevantComponentIds.map(componentId =>
      fields.map(field =>
        getDataPoint(
          components,
          componentId,
          field,
          childAggregationFunction,
          isFlipped,
          i18nLocale
        )
      )
    );
  }

  return fields.map(field =>
    relevantComponentIds.map(componentId =>
      getDataPoint(
        components,
        componentId,
        field,
        childAggregationFunction,
        isFlipped,
        i18nLocale
      )
    )
  );
};

type GetChildComponentIdsArgs = {
  components: ComponentsState;
  includeAllDescendants: boolean;
  workspaceId: ArdoqId;
  selectedComponentId?: ArdoqId;
  includedComponentIds: Set<ArdoqId>;
};

export const getChildComponentIds = ({
  components,
  includeAllDescendants,
  selectedComponentId,
  workspaceId,
  includedComponentIds,
}: GetChildComponentIdsArgs): ArdoqId[] => {
  const directChildren = selectedComponentId
    ? (components.childrenByParent.get(selectedComponentId) ?? [])
    : componentOperations.getRootComponents(workspaceId, components.models);

  const expanded = includeAllDescendants
    ? componentOperations.getFlatHierarchy(
        directChildren,
        components.childrenByParent
      )
    : directChildren;

  return toIds(expanded).filter(componentId =>
    includedComponentIds.has(componentId)
  );
};

type GetLegendRowsParams = {
  sideways: boolean;
  workspaceId: ArdoqId;
  relevantComponentIds: ArdoqId[];
  components: ComponentsState;
  selectedFields: APIFieldAttributes[];
};

const getLegendContent = (labels: string[]): ComponentTypeForLegend[] =>
  labels.map((label, index) => ({
    label: label || '',
    color: getRangeColor(index),
    representationData: {
      isImage: false,
      value: null,
    },
  }));

const getDisplayName = (
  components: ComponentsState,
  componentId: ArdoqId
): string | null => components.byId[componentId]?.name ?? null;

export const getLegendRows = ({
  sideways,
  workspaceId,
  relevantComponentIds,
  components,
  selectedFields,
}: GetLegendRowsParams): ViewLegendRowModel[] => {
  const title = sideways ? 'Components' : 'Values';

  const legendLabels = sideways
    ? relevantComponentIds
        .map(componentId => getDisplayName(components, componentId))
        .filter(isTruthy)
    : selectedFields.map(field => field.label);

  return [
    {
      title,
      id: workspaceId,
      sections: [
        {
          heading: null,
          items: getLegendContent(legendLabels),
        },
      ],
    },
  ];
};
